import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import {
  createAccessReview, getAccessReviewCampaigns, getAccessReviewCampaign,
  decideAccessReviewItem, completeAccessReview, cancelAccessReview, getAttestationCertificate,
} from "../api/adminApi";
import { getGroups } from "../api/groupRightsApi";
import { LicenseArt } from "../components/adminSurface/heroArt";

function fmtDate(raw) {
  if (!raw) return "";
  try { const d = new Date(raw); if (isNaN(d)) return String(raw);
    const p = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  } catch { return String(raw); }
}

const ARV_STYLES = `
.arv-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.arv-hero__glow{ position:absolute; inset:0; pointer-events:none; background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.arv-hero__icon{ position:relative; width:54px; height:54px; border-radius:15px; flex:0 0 auto; background:rgba(255,255,255,.14);
  display:flex; align-items:center; justify-content:center; font-size:27px; box-shadow:inset 0 0 0 1px rgba(255,255,255,.2); }
.arv-hero__body{ position:relative; flex:1; min-width:0; }
.arv-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.arv-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.arv-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.arv-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.arv-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase; letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.arv-hero__dot{ width:6px; height:6px; border-radius:50%; background:#fbbf24; }
.arv-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.arv-hero__logo svg{ display:block; height:82px; width:auto; }
.arv-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  display:flex; align-items:center; justify-content:center; }
.arv-refresh:hover{ background:rgba(255,255,255,.24); } .arv-refresh:disabled{ opacity:.6; cursor:not-allowed; }
.arv-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px; box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.arv-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.arv-field{ display:flex; flex-direction:column; min-width:0; }
.arv-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.arv-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; }
.arv-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.arv-seg{ display:inline-flex; background:#f1f5f9; border:1px solid #e6eaf1; border-radius:11px; padding:3px; gap:2px; }
.arv-seg button{ appearance:none; border:none; background:transparent; color:#475569; font-size:12.5px; font-weight:600; padding:8px 14px; border-radius:8px; cursor:pointer; white-space:nowrap; }
.arv-seg button.on{ background:#fff; color:#6d28d9; box-shadow:0 1px 3px rgba(15,23,42,.14); }
.arv-seg.sm button{ padding:6px 12px; font-size:12px; }
.arv-seg button.keep.on{ color:#15803d; } .arv-seg button.revoke.on{ color:#b91c1c; }
.arv-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 22px; border-radius:12px;
  background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed); box-shadow:0 8px 20px -4px rgba(109,40,217,.42); display:inline-flex; align-items:center; gap:8px; }
.arv-cta:hover{ filter:brightness(1.06); } .arv-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }
.arv-cta.ghost{ background:#f1f5f9; color:#4338ca; box-shadow:none; border:1px solid #e2e8f0; }
.arv-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden; box-shadow:0 10px 34px -14px rgba(15,23,42,.12); margin-bottom:16px; }
.arv-row td{ transition:background .12s; } .arv-row:hover td{ background:#faf7ff !important; } .arv-row.sel td{ background:#f3f0ff !important; }
.arv-chip{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:600; background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.arv-status{ display:inline-flex; align-items:center; gap:6px; padding:3px 10px 3px 8px; border-radius:999px; font-size:11px; font-weight:700; }
.arv-status__dot{ width:6px; height:6px; border-radius:50%; background:currentColor; }
.arv-status--open{ background:#fef9c3; color:#a16207; } .arv-status--completed{ background:#dcfce7; color:#15803d; } .arv-status--cancelled{ background:#f1f5f9; color:#64748b; }
.arv-status--open .arv-status__dot{ animation:arv-pulse 1.9s ease-in-out infinite; }
@keyframes arv-pulse{ 0%,100%{opacity:1;} 50%{opacity:.35;} }
.arv-empty{ padding:56px 40px; text-align:center; }
.arv-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center; justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }
.arv-verify{ display:inline-flex; align-items:center; gap:7px; padding:5px 12px; border-radius:999px; font-size:12px; font-weight:700; }
.arv-verify.ok{ background:#dcfce7; color:#15803d; } .arv-verify.bad{ background:#fee2e2; color:#b91c1c; }
.arv-stale{ color:#b45309; font-weight:600; }
.arv-dd{ position:relative; }
.arv-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px; font-family:inherit; cursor:pointer; }
.arv-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); } .arv-dd__btn:disabled{ opacity:.55; cursor:not-allowed; }
.arv-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; } .arv-dd__lbl.ph{ color:#94a3b8; }
.arv-dd__chev{ font-size:11px; color:#94a3b8; } .arv-dd__btn.open .arv-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.arv-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box; background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3); padding:6px; max-height:280px; overflow-y:auto; }
.arv-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px; color:#334155; cursor:pointer; }
.arv-dd__opt:hover{ background:#f6f2ff; } .arv-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
`;

function Dropdown({ value, onChange, options, placeholder, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="arv-dd" ref={ref}>
      <button type="button" className={`arv-dd__btn${open ? " open" : ""}`} disabled={disabled} onClick={() => setOpen((o) => !o)}>
        <span className={`arv-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="arv-dd__chev">▾</span>
      </button>
      {open && (
        <div className="arv-dd__menu">
          {options.map((o) => (
            <div key={o.value} className={`arv-dd__opt${String(o.value) === String(value) ? " sel" : ""}`}
              onClick={() => { onChange(o.value); setOpen(false); }}>{o.label}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AccessReviewPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [campaigns, setCampaigns] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [name, setName] = useState("");
  const [scope, setScope] = useState("Organization");
  const [roleId, setRoleId] = useState("");
  const [creating, setCreating] = useState(false);

  const [detail, setDetail] = useState(null);   // { campaign, items }
  const [busy, setBusy] = useState(false);
  const [cert, setCert] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [cs, gs] = await Promise.all([
        getAccessReviewCampaigns(token).catch(() => ({ items: [] })),
        getGroups(token).catch(() => []),
      ]);
      setCampaigns(Array.isArray(cs?.items) ? cs.items : (Array.isArray(cs) ? cs : []));
      setRoles(Array.isArray(gs) ? gs : (gs?.groups ?? []));
    } catch (e) { setError(e?.message || t("accessReview.loadFailed")); }
    finally { setLoading(false); }
  }, [token, t]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (creating) return;
    if (!name.trim() || (scope === "Role" && !roleId)) { setError(t("accessReview.nameRequired")); return; }
    setCreating(true); setError("");
    try {
      await createAccessReview(token, { name: name.trim(), scope, roleId: scope === "Role" ? Number(roleId) : null });
      setName(""); setRoleId("");
      await load();
    } catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
    finally { setCreating(false); }
  };

  const openDetail = async (id) => {
    setError(""); setCert(null);
    try { setDetail(await getAccessReviewCampaign(token, id)); }
    catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
  };

  const decide = async (item, decision) => {
    setBusy(true); setError("");
    try {
      await decideAccessReviewItem(token, item.id, decision, null);
      await openDetail(detail.campaign.id);
    } catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
    finally { setBusy(false); }
  };

  const complete = async () => {
    if (busy) return;
    setBusy(true); setError("");
    try { await completeAccessReview(token, detail.campaign.id); await load(); await openDetail(detail.campaign.id); }
    catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
    finally { setBusy(false); }
  };

  const cancel = async (id) => {
    setBusy(true); setError("");
    try { await cancelAccessReview(token, id); await load(); if (detail?.campaign?.id === id) setDetail(null); }
    catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
    finally { setBusy(false); }
  };

  const showCert = async (id) => {
    setError(""); setCert(null);
    try { setCert(await getAttestationCertificate(token, id)); await openDetail(id); }
    catch (e) { setError(t("accessReview.actionFailed", { error: e?.message || String(e) })); }
  };

  const roleOpts = useMemo(() => roles.map((r) => ({ value: String(r.id ?? r.Id), label: r.name ?? r.Name ?? r.role ?? r.Role ?? String(r.id ?? r.Id) })), [roles]);
  const statusLabel = (s) => t(`accessReview.status${s}`, { defaultValue: s });
  const pendingCount = detail ? detail.items.filter((i) => i.decision === "Pending").length : 0;
  const openCount = useMemo(() => campaigns.filter((c) => c.status === "Open").length, [campaigns]);

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{ARV_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <div className="arv-hero">
          <div className="arv-hero__glow" />
          <div className="arv-hero__logo"><LicenseArt /></div>
          <div className="arv-hero__body">
            <h1 className="arv-hero__title">{t("accessReview.title")}</h1>
            <p className="arv-hero__sub">{t("accessReview.subtitle")}</p>
          </div>
          <div className="arv-hero__stat">
            <b>{openCount}</b>
            <span><i className="arv-hero__dot" /> {t("accessReview.statusOpen")}</span>
          </div>
          <button className="arv-refresh" onClick={load} disabled={loading} title={t("accessReview.refresh")}>{loading ? "⟳" : "↺"}</button>
        </div>

        {/* New review */}
        <div className="arv-card">
          <div className="arv-grid">
            <div className="arv-field" style={{ flex: "2 1 240px" }}>
              <label className="arv-lbl">{t("accessReview.name")}</label>
              <input className="arv-input" type="text" placeholder={t("accessReview.namePlaceholder")} value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="arv-field" style={{ flex: "0 0 auto" }}>
              <label className="arv-lbl">{t("accessReview.scope")}</label>
              <div className="arv-seg">
                <button className={scope === "Organization" ? "on" : ""} onClick={() => setScope("Organization")}>{t("accessReview.scopeOrg")}</button>
                <button className={scope === "Role" ? "on" : ""} onClick={() => setScope("Role")}>{t("accessReview.scopeRole")}</button>
              </div>
            </div>
            {scope === "Role" && (
              <div className="arv-field" style={{ flex: "1 1 200px" }}>
                <label className="arv-lbl">{t("accessReview.role")}</label>
                <Dropdown value={roleId} onChange={setRoleId} options={roleOpts}
                  placeholder={roleOpts.length ? t("accessReview.selectRole") : t("accessReview.noRoles")} disabled={!roleOpts.length} />
              </div>
            )}
            <button className="arv-cta" onClick={create} disabled={creating || !name.trim() || (scope === "Role" && !roleId)}>
              🧾 {creating ? t("accessReview.creating") : t("accessReview.create")}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ color: "#b91c1c", background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "11px 16px", marginBottom: 12, fontSize: 13 }}>⚠ {error}</div>
        )}

        {/* Campaigns */}
        <div className="arv-tablecard" style={{ overflowX: "auto" }}>
          {campaigns.length === 0 && !loading ? (
            <div className="arv-empty">
              <div className="arv-empty__ring">🧾</div>
              <div style={{ fontWeight: 700, fontSize: 16, color: "#0f172a" }}>{t("accessReview.noReviews")}</div>
              <div style={{ fontSize: 13, color: "#64748b", marginTop: 6 }}>{t("accessReview.noReviewsHint")}</div>
            </div>
          ) : (
            <table className="logs-table" style={{ minWidth: 820 }}>
              <thead><tr>
                <th style={{ paddingLeft: 18 }}>{t("accessReview.colName")}</th>
                <th style={{ width: 150 }}>{t("accessReview.colScope")}</th>
                <th style={{ width: 120 }}>{t("accessReview.colStatus")}</th>
                <th style={{ width: 160 }}>{t("accessReview.colProgress")}</th>
                <th style={{ width: 150 }}>{t("accessReview.colCreated")}</th>
                <th style={{ width: 180 }}></th>
              </tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr className={`arv-row${detail?.campaign?.id === c.id ? " sel" : ""}`} key={c.id}>
                    <td style={{ paddingLeft: 18, fontSize: 13, color: "#0f172a", fontWeight: 500 }}>{c.name}</td>
                    <td><span className="arv-chip">{c.scope === "Role" ? `${t("accessReview.scopeRole")}: ${c.scopeRoleName ?? c.scopeRoleId}` : t("accessReview.scopeOrg")}</span></td>
                    <td><span className={`arv-status arv-status--${String(c.status).toLowerCase()}`}><span className="arv-status__dot" />{statusLabel(c.status)}</span></td>
                    <td style={{ fontSize: 12.5, color: "#334155" }}>
                      {c.status === "Completed"
                        ? <span><b style={{ color: "#15803d" }}>{c.keptItems} {t("accessReview.kept")}</b> · <b style={{ color: "#b91c1c" }}>{c.revokedItems} {t("accessReview.revoked")}</b></span>
                        : `${c.totalItems} ${t("accessReview.total")}`}
                    </td>
                    <td style={{ fontSize: 12, color: "#64748b" }}>{fmtDate(c.createdAt)}{c.createdBy ? ` ${t("accessReview.by", { user: c.createdBy })}` : ""}</td>
                    <td>
                      <div style={{ display: "flex", gap: 12 }}>
                        {c.status === "Open" && <button className="link-btn" style={{ fontSize: 13, color: "#6d28d9" }} onClick={() => openDetail(c.id)}>{t("accessReview.review")}</button>}
                        {c.status === "Open" && <button className="link-btn" style={{ fontSize: 13, color: "#64748b" }} onClick={() => cancel(c.id)}>{t("accessReview.cancel")}</button>}
                        {c.hasCertificate && <button className="link-btn" style={{ fontSize: 13, color: "#6d28d9" }} onClick={() => showCert(c.id)}>🏅 {t("accessReview.certificate")}</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Detail: items + complete, or certificate */}
        {detail && (
          <div className="arv-card">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
              <b style={{ fontSize: 15, color: "#0f172a" }}>{detail.campaign.name}</b>
              <span className={`arv-status arv-status--${String(detail.campaign.status).toLowerCase()}`}><span className="arv-status__dot" />{statusLabel(detail.campaign.status)}</span>
              {cert && <span className={`arv-verify ${cert.verified ? "ok" : "bad"}`}>{cert.verified ? `✓ ${cert.certNumber}` : `✗ ${t("accessReview.certInvalid")}`}</span>}
              <button className="link-btn" style={{ fontSize: 13, marginLeft: "auto" }} onClick={() => { setDetail(null); setCert(null); }}>{t("accessReview.close")}</button>
            </div>

            {cert ? (
              <div style={{ fontSize: 13, color: "#334155", fontFamily: "ui-monospace, monospace", lineHeight: 1.9 }}>
                <div>{t("accessReview.certScope")}: {cert.payload?.scope}{cert.payload?.scopeRole ? ` (${cert.payload.scopeRole})` : ""}</div>
                <div><b style={{ color: "#15803d" }}>{cert.payload?.kept} {t("accessReview.kept")}</b> · <b style={{ color: "#b91c1c" }}>{cert.payload?.revoked} {t("accessReview.revoked")}</b> / {cert.payload?.totalGrants} {t("accessReview.total")}</div>
                <div>{t("accessReview.complete")}: {cert.payload?.completedBy} · {fmtDate(cert.payload?.completedAt)}</div>
                <div style={{ color: "#94a3b8" }}>{cert.payload?.algorithm} · {cert.payload?.keyId} · chain #{cert.auditChainId}</div>
              </div>
            ) : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table className="logs-table" style={{ minWidth: 720 }}>
                    <thead><tr>
                      <th style={{ paddingLeft: 4 }}>{t("accessReview.colUser")}</th>
                      <th style={{ width: 170 }}>{t("accessReview.colRole")}</th>
                      <th style={{ width: 150 }}>{t("accessReview.colLastAccess")}</th>
                      <th style={{ width: 200 }}>{t("accessReview.colDecision")}</th>
                    </tr></thead>
                    <tbody>
                      {detail.items.map((i) => {
                        const stale = !i.lastAccessAt;
                        return (
                          <tr className="arv-row" key={i.id}>
                            <td style={{ fontSize: 13, color: "#0f172a" }}>{i.userName}</td>
                            <td><span className="arv-chip">{i.roleName ?? i.roleId}</span></td>
                            <td style={{ fontSize: 12.5 }} className={stale ? "arv-stale" : ""}>{i.lastAccessAt ? fmtDate(i.lastAccessAt) : t("accessReview.never")}</td>
                            <td>
                              {detail.campaign.status === "Open" ? (
                                <div className="arv-seg sm">
                                  <button className={`keep ${i.decision === "Keep" ? "on" : ""}`} disabled={busy} onClick={() => decide(i, "Keep")}>✓ {t("accessReview.keep")}</button>
                                  <button className={`revoke ${i.decision === "Revoke" ? "on" : ""}`} disabled={busy} onClick={() => decide(i, "Revoke")}>✕ {t("accessReview.revoke")}</button>
                                </div>
                              ) : (
                                <span style={{ fontSize: 12.5, fontWeight: 600, color: i.decision === "Revoke" ? "#b91c1c" : (i.decision === "Keep" ? "#15803d" : "#64748b") }}>{t(`accessReview.${(i.decision || "pending").toLowerCase()}`, { defaultValue: i.decision })}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {detail.campaign.status === "Open" && (
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14, flexWrap: "wrap" }}>
                    {pendingCount > 0 && <span style={{ fontSize: 12.5, color: "#a16207" }}>⚠ {t("accessReview.pendingWarn", { count: pendingCount })}</span>}
                    <button className="arv-cta" style={{ marginLeft: "auto" }} onClick={complete} disabled={busy || pendingCount > 0}>
                      🏅 {busy ? t("accessReview.completing") : t("accessReview.complete")}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
